import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Container from "@/components/base/Container";
import TagPill from "@/components/base/TagPill";
import DimensionBars from "@/components/feature/DimensionBars";
import CompareCard from "@/components/feature/CompareCard";
import { compareItems } from "@/mocks/compareData";

export default function CompareDetailPage() {
  const { id } = useParams();
  const item = compareItems.find((entry) => entry.id === id);

  useSeo({
    title: item
      ? `${item.title} | 咖位对比-咖位观察局`
      : "对比详情未找到-咖位观察局",
    description:
      item?.summary ??
      "未找到对应的对比内容，返回对比榜单查看更多网红明星跨界对比。",
    keywords: item ? item.tags.join(",") : "网红明星对比,咖位对比",
    type: "article",
    jsonLd:
      item && {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            headline: item.title,
            description: item.summary,
            datePublished: item.date,
            author: { "@type": "Organization", name: item.author },
            articleSection: item.category,
            inLanguage: "zh-CN",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "对比榜单", item: "/compare" },
              { "@type": "ListItem", position: 3, name: item.title },
            ],
          },
        ],
      },
  });

  if (!item) {
    return (
      <>
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <Container>
            <div className="py-24 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没找到这组对比</h1>
              <p className="mt-3 text-sm text-foreground-600">它可能已经被合并或调整了位置，去榜单里找找看。</p>
              <Link
                to="/compare"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回对比榜单
              </Link>
            </div>
          </Container>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = compareItems.filter((entry) => entry.id !== item.id).slice(0, 3);

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs
          items={[
            { label: "首页", path: "/" },
            { label: "对比榜单", path: "/compare" },
            { label: item.title },
          ]}
        />

        <article className="bg-background-50">
          <Container>
            <header className="mx-auto max-w-3xl py-12 text-center md:py-16">
              <TagPill variant="primary">{item.category}</TagPill>
              <h1 className="mt-5 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">{item.summary}</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-foreground-500">
                <span>{item.author}</span>
                <span className="h-3 w-px bg-background-300" />
                <span>{item.date}</span>
                <span className="h-3 w-px bg-background-300" />
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                    <i className="ri-fire-line text-base" />
                  </span>
                  热度 {item.heat.toLocaleString()}
                </span>
              </div>
            </header>
          </Container>

          <Container>
            <div className="mx-auto h-64 w-full max-w-5xl overflow-hidden rounded-lg md:h-[420px]">
              <img
                src={item.coverImage}
                alt={`${item.title}对比场景示意图`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Container>

          <Container>
            <div className="mx-auto mt-12 max-w-5xl">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">维度对比</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  左右两侧为同一维度下的相对强度，条越长代表该维度表现越突出。数值为相对值，用于横向比较。
                </p>
                <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <DimensionBars
                    dimensions={item.dimensions}
                    leftName={item.leftName}
                    rightName={item.rightName}
                  />
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
                  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-primary-700">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-scales-3-line text-base" />
                    </span>
                    {item.leftName}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                    在热度与话题维度上占据优势，擅长在即时场景里带动讨论、放大声量，是内容场里的节奏掌控方。
                  </p>
                </div>
                <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6">
                  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-wide text-secondary-700">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-award-line text-base" />
                    </span>
                    {item.rightName}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                    在专业与形象维度上更为稳固，擅长积累长期信用与作品口碑，是品牌与正剧场域里的稳定选项。
                  </p>
                </div>
              </div>

              <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-100 p-6">
                <p className="text-xs font-medium tracking-wide text-primary-600">观察结论</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700 md:text-base">{item.verdict}</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <TagPill key={tag} variant="outline">
                    #{tag}
                  </TagPill>
                ))}
              </div>
            </div>
          </Container>
        </article>

        <section className="border-t border-background-200/80 bg-background-100 py-14 md:py-16">
          <Container>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续对比</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {related.map((entry) => (
                <CompareCard
                  key={entry.id}
                  id={entry.id}
                  title={entry.title}
                  category={entry.category}
                  summary={entry.summary}
                  coverImage={entry.coverImage}
                  leftName={entry.leftName}
                  rightName={entry.rightName}
                  heat={entry.heat}
                  date={entry.date}
                />
              ))}
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}